import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';
import { Howl } from 'howler';
import * as _ from 'underscore';
import { UsersService } from 'src/app/users/users.service';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from 'src/app/shared/services/common.service';

/**
 * One group card on the public ranking page. A group can hold more than one Ready to Pick
 * battery — how many is the site's Ready to Pick Queue Count, so `locations` is 0, 1 or 2
 * battery IDs (ST-2328).
 */
interface PublicRankingGroup {
	name: string;
	locations: string[];
	/** The previous render's batteries, kept so the card can animate the change. */
	lastLocations: string[];
	/** What the card actually renders — falls back to `lastLocations` while it fades out. */
	displayLocations: string[];
	hasChanged: boolean;
}

@Component({
	selector: 'app-charglink-brm-public',
	templateUrl: './charglink-brm-public.component.html',
	styleUrls: ['./charglink-brm-public.component.scss']
})
export class CharglinkBrmPublicComponent implements OnInit, OnDestroy {
	groups = [];
	@Input() isTabMode = false;
	@Input() siteId = 0;
	customerId = 0;
	siteURL = '/';
	token = '';
	data = [];
	dataSorted = [];
	timeoutId;

	// buzzer sound
	buzzing: boolean = false;
	sound: Howl;
	buzzerEnabled: boolean = true;
	getChargLinkGroupsData: any;

	refreshTimerId;
	failedDataFetchCounter: number = 0;
	getRankEnables: boolean = true;
	isLoggedIn: boolean = false;
	isExpiredToken: boolean = false;
	lastLocationByGroupName = {}; // used for animation
	siteSub: Subscription = new Subscription();

	constructor(
		private sideMenuService: SideMenuService,
		private route: ActivatedRoute,
		private sitesService: SitesService,
		private cdref: ChangeDetectorRef,
		private router: Router,
		private usersService: UsersService,
		private navigationService: NavigationService,
		private translateService: TranslateService,
		private commonService: CommonService
	) {}

	ngOnInit() {
		if (!this.isTabMode) {
			this.token = this.route.snapshot.params['token'];
			this.siteId = this.route.snapshot.queryParams['siteId'] || 0;
		}
		const currentUser = this.usersService.getCurrentUser();
		this.siteSub = this.sideMenuService.currentSite.subscribe((siteInfo: any) => {
			if (!siteInfo || !siteInfo['id'])
				return;
			this.commonService.setPageTabTitle(`${siteInfo.customer_name} - ${siteInfo.name} ${this.translateService.instant('nav.ranking_page')}`);
		});
		if (currentUser?.id)
			this.isLoggedIn = true;

		this.initSound();

		if (!this.isTabMode)
			this.sideMenuService.hide();

		this.getChargLinkGroups(false);

		if (!this.isTabMode)
			this.startPageRefreshTimer();
	}

	startPageRefreshTimer() {
		this.refreshTimerId = setInterval(() => {
			window.location.reload();
		}, 15*60*1000);
	}

	getChargLinkGroups(noUiBlock=true) {
		let checkForUpdate = true;

		const filters = {
			siteId: this.siteId,
			token: this.token,
			fromDate: '',
			toDate: '',
			isHistory: false,
			publicPageReq: true
		};

		this.sitesService.getBRMGroupsData(filters, noUiBlock).subscribe((data: any) => {
			if ('siteId' in data)
				return this.updateSiteId(data.siteId);

			if (this.failedDataFetchCounter) {
				this.failedDataFetchCounter = 0;
				this.setPageAsConnectivityStatus(true);
			}

			data = this.mapResponseData(data);
			
			if (data.site && data.site.id && data.site.customerid)
				this.siteURL = `/${data.site.customerid}/${data.site.id}`;

			let groups = data.groups;

			// Remove empty groups
			groups = _.filter(groups, function(group){ return group['groupname'];})

			if (!groups.length)
				return this.repeatGetChargLinkGroups();

			for (const group of groups) {
				group['groupname'] = group['groupname'].toUpperCase();
				const {thisGroup, checkUpdate} = this.getGroupDataById(group.id, data, checkForUpdate);
				checkForUpdate = checkUpdate;
				const statuses = group.innergroups;

				for (const status in statuses) {
					let thisGroupStatusChargersById = {};
					let oldGroupIds = [];
					let newGroupIds = [];

					const groupStatuses = group['innergroups'][status];
					const oldGroupStatuses = thisGroup?.['innergroups']?.[status] || [];

					if (checkForUpdate) {
						oldGroupIds = _.pluck(oldGroupStatuses, 'id');
						newGroupIds = _.pluck(groupStatuses, "id");

						checkForUpdate = this.updateDomIfFindChange(oldGroupIds, newGroupIds, data);
					}

					thisGroupStatusChargersById = _.indexBy(oldGroupStatuses, 'id');
					for (const charger of groupStatuses) {
						if (checkForUpdate)
							this.updateChargesData(charger, thisGroupStatusChargersById, data);

					}
				}

				if (this.buzzerEnabled)
					this.checkAndPlayBuzzer(group);

				this.dataSorted = _.sortBy(this.data, ['innergroups[\'ready\']']).reverse();
			}
			this.prepareGroupsData();

			this.repeatGetChargLinkGroups();
		}, () => {
			this.failedDataFetchCounter++;
			if (this.failedDataFetchCounter == 30)
				this.setPageAsConnectivityStatus(false);

			this.repeatGetChargLinkGroups();
		});
	}

	updateSiteId(siteId: number) {
		if (this.isTabMode)
			return;

		if (!siteId)
			return this.isExpiredToken = true;

		this.router.navigate(
			['charglink-brm', 'public', this.token],
			{ queryParams: {'siteId': siteId}, replaceUrl: true }
		);

		this.siteId = siteId;

		this.getChargLinkGroups();
		return;
	}

	repeatGetChargLinkGroups() {
		this.getChargLinkGroupsData = setTimeout(() => {
			if(this.getRankEnables)
				this.getChargLinkGroups();
		}, 3000);
	}

	mapResponseData(data: any) {
		const mappedData: any = {};
		mappedData.site = {
			customerid: data.s.cid,
			id: data.s.id,
		};

		mappedData.groups = [];

		data.g.forEach((group) => {
			const chargers = [];
			group.c.forEach((charger) => {
				chargers.push({
					id: charger.id,
					alertFlag: charger.af,
					currentSOC: charger.csoc,
					connected: charger.c,
					name: charger.n
				});
			});

			mappedData.groups.push(
				{
					groupname: group.gn,
					id: group.id,
					innergroups: {
						ready: chargers
					},
					alertFlag: group.af,
				}
			);

		});

		return mappedData;
	}

	getGroupDataById(id: number, data: any, checkForUpdate: boolean) {
		let thisGroup = {};

		for (const dataGroup of this.data) {
			if (dataGroup.id == id) {
				thisGroup = dataGroup;
				break;
			}
		}

		if (!thisGroup['id']) {
			this.data = data['groups'];
			checkForUpdate = false;
		}

		return {thisGroup, checkUpdate: checkForUpdate};
	}

	updateDomIfFindChange(oldIds, newIds, data) {
		let checkForUpdate = true;

		// Both directions matter: a battery being added to the ready list is as much a change as
		// one being removed, and a group can now hold two of them (ST-2328).
		const areEquals = _.isEmpty(_.difference(oldIds, newIds)) && _.isEmpty(_.difference(newIds, oldIds));
		if (!areEquals) {
			this.data = data['groups'];
			checkForUpdate = false;
			this.cdref.detectChanges();
		}

		return checkForUpdate;
	}

	updateChargesData(charger, thisGroupStatusChargersById, data) {
		for (const key in charger) {
			// update data if there is change in chargers list except for these attributes: ['relaxedBatteryTimer', 'time'];
			if (!['relaxedBatteryTimer', 'time'].includes(key)) {
				if (!thisGroupStatusChargersById[charger.id] || thisGroupStatusChargersById[charger.id][key] != charger[key]) {
					this.data = data.groups;
					break;
				}
			}
		}
	}

	checkAndPlayBuzzer(charger) {
		if (!this.buzzing && charger.alertFlag) {
			this.playBuzzer();
		}
	}

	toggleBuzzer() {
		this.buzzerEnabled = !this.buzzerEnabled;

		if (this.buzzerEnabled)
			this.sitesService.enableBuzzer();
		else
			this.sound.stop();
	}

	initSound() {
		const pc = this;

		this.sound = new Howl({
			src: ['/sctportal/audio/buzz.mp3'],
			format: ['mp3'],
			autoUnlock: true,
			loop: true,

			onplayerror: function() {
				this.sound.once('unlock', function() {
					pc.playBuzzer();
				});
			}
		});
	}

	playBuzzer() {
		const buzzerTime = 16000;
		this.sound.play();
		this.buzzing = true;

		this.timeoutId = setTimeout(() => {
			this.buzzing = false;
			this.sound.stop();
		}, buzzerTime);
	}

	prepareGroupsData() {
		const groups: PublicRankingGroup[] = [];
		const groupObjects = _.indexBy(this.groups, 'name');
		for (const data of this.dataSorted) {
			if (!data.groupname)
				continue;

			// Every Ready to Pick battery in the group gets its own box. How many there can be is
			// the site's Ready to Pick Queue Count, so this is 0, 1 or 2 entries (ST-2328).
			// A battery with no ID is never dropped — the engine says it is ready, so it must be
			// shown; a missing ID is a data problem to surface, not a reason to hide a battery.
			const locations = (data.innergroups?.ready || []).map(charger => charger.name || '');
			const previousGroup = groupObjects[data.groupname];
			// A group seen for the first time must not animate on its first paint.
			const lastLocations = previousGroup ? (previousGroup.locations || []) : locations;

			groups.push({
				name: data.groupname,
				locations,
				lastLocations, // used for animation
				// Keeps the outgoing batteries on screen while the card fades out, mirroring the
				// `location || lastLocation` fallback this replaced.
				displayLocations: locations.length ? locations : lastLocations,
				hasChanged: locations.join('|') != lastLocations.join('|'),
			});
		}

		this.groups = groups;
	}

	setPageAsConnectivityStatus(isOnline: boolean) {
		if (!isOnline) {
			clearInterval(this.refreshTimerId)
			return;
		}

		if (!this.refreshTimerId && !this.isTabMode)
			this.startPageRefreshTimer();
	}

	ngOnDestroy() {
		if (!this.isTabMode)
			this.sideMenuService.show();
		
		if (this.sound)
			this.sound.stop();

		if(this.timeoutId)
			clearTimeout(this.timeoutId);

		if(this.getChargLinkGroupsData)
			clearTimeout(this.getChargLinkGroupsData);

		this.getRankEnables = false;
		if (this.refreshTimerId)
			clearInterval(this.refreshTimerId);
	}

	trackByGroup(group: any): string {
		return group.name;
	}
}
